// app/components/dispensary-near-me-page.tsx
//
// "Dispensary Near [City]" landing page (SEO conquest pages).
// Pattern: /dispensary-locations/near-[city]-ca   e.g. /dispensary-locations/near-la-jolla-ca
//
// Rendered from inside the existing `_layout.dispensary-locations.$location/route.tsx`
// loader/component when the slug starts with `near-`. We piggy-back on that route
// rather than registering a new file because Remix v2's flat-routes convention
// can't express a "literal-prefix + dynamic" segment like `near-$city`.
//
// The `NearMePageData` shape mirrors the final Supabase rows
// (`website_nearby_dispensaries` + `website_nearby_dispensary_faqs`, brand='catalyst'),
// so swapping the dummy data for a Supabase fetch is a one-spot change.

import { useEffect } from 'react';
import { Link } from '@remix-run/react';
import Breadcrumbs from '~/components/breadcrumbs';
import PromotionalCarousel from '~/components/promotional-carousel';
import type { Carousel } from '~/types/promotional-carousel';
import FaqsSection from '../routes/_layout.dispensary-locations.$location/components/faqs-section';
import { generateLocationSchema } from '~/utils/schema-utils';

export interface NearMeFaq {
  question: string;
  answer: string;
}

export interface NearMePageData {
  slug: string;
  /** Store Central + website_locations data for the referred-to store's JSON-LD. */
  storeSchemaData?: {
    addressParts: { street: string; state: string; city: string; zip: string; county?: string } | null;
    geo: { lat: number; lng: number } | null;
    mapsUrl: string | null;
    email: string | null;
    ratingValue: number | null;
    reviewCount: number | null;
  };
  city: string; // human-readable, e.g. "La Jolla"
  matchedLocationSlug: string; // e.g. "san-diego-dispensary"
  matchedLocationName: string; // e.g. "Catalyst San Diego"
  matchedLocationAddress: string;
  matchedLocationHours: string;
  matchedLocationPhone: string;
  matchedLocationMapEmbed: string;
  matchedLocationDirectionsUrl: string;
  matchedLocationReviewsWidget: string; // raw HTML containing <div class="elfsight-app-...">; '' if none
  seoTitle: string;
  seoDescription: string;
  ogTitle: string;
  header: string;
  introText: string;
  directionsHeader: string;
  directionsContent: string; // HTML
  box1Header: string;
  box1Content: string; // HTML
  box2Header: string;
  box2Content: string; // HTML
  box3Header: string;
  box3Content: string; // HTML
  box4Header: string;
  box4Content: string; // HTML
  faqs: NearMeFaq[];
}

export const DUMMY_NEAR_ME_PAGE: NearMePageData = {
  slug: 'la-jolla',
  city: 'La Jolla',
  matchedLocationSlug: 'san-diego-dispensary',
  matchedLocationName: 'Catalyst San Diego',
  matchedLocationAddress: '5125 Convoy St, STE 211, San Diego, CA 92111',
  matchedLocationHours: 'Open 9am - 9.30pm',
  matchedLocationPhone: '(858) 277-4200',
  matchedLocationDirectionsUrl:
    'https://www.google.com/maps/dir/?api=1&destination=5125+Convoy+St+STE+211+San+Diego+CA+92111',
  matchedLocationMapEmbed:
    '<iframe src="https://www.google.com/maps?q=5125+Convoy+St+STE+211+San+Diego+CA+92111&output=embed" width="100%" height="412" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>',
  matchedLocationReviewsWidget: '',
  seoTitle: 'Dispensary near La Jolla, CA | Catalyst Cannabis',
  seoDescription:
    'Looking for a top-rated dispensary near La Jolla? Get directions, shop premium cannabis, and visit Catalyst San Diego today!',
  ogTitle: 'Dispensary near La Jolla, CA | Catalyst Cannabis',
  header: 'Dispensary Near La Jolla, CA',
  introText:
    "Searching for a dispensary near me in La Jolla? Catalyst Cannabis is one of the closest and most trusted dispensaries for premium cannabis at fair, transparent prices. Just minutes from La Jolla's beaches, shopping districts, and scenic coastlines, our San Diego dispensary offers a curated menu of flower, carts, concentrates, edibles, tinctures, and pre-rolls — all vetted for quality and value.",
  directionsHeader: 'How to Get to Catalyst from La Jolla',
  directionsContent: `
    <p>Reaching Catalyst from La Jolla is fast and convenient. Most customers take:</p>
    <ul>
      <li>I-5 South → 52 East → 15 South, or</li>
      <li>I-5 South → 163 South, depending on where they're coming from.</li>
    </ul>
    <p>The drive typically takes 15–20 minutes, making Catalyst one of the most accessible licensed cannabis options for La Jolla residents, UCSD students, visitors, and professionals working in the Golden Triangle.</p>
    <p>Get turn-by-turn directions here: <a href="/dispensary-locations">find a dispensary</a>.</p>
  `,
  box1Header: 'Shop Cannabis Products Near La Jolla',
  box1Content: `
    <p>Looking for pre-rolls, THC edibles, THC carts, or premium THC concentrates? Catalyst carries one of the strongest cannabis selections in Southern California — designed for both everyday consumers and connoisseurs.</p>
    <p>Our San Diego menu includes:</p>
    <ul>
      <li>Premium indoor, exotic, and craft flower</li>
      <li>Infused pre-rolls, multi-packs &amp; pre rolled cones</li>
      <li>Live resin, rosin, badder, crumble &amp; solventless concentrates</li>
      <li>A wide range of CBD tinctures and wellness products</li>
      <li>Potent edibles for every tolerance level</li>
      <li>Cartridges, pods, and disposables from California's top brands</li>
    </ul>
    <p>La Jolla customers often visit Catalyst before or after a beach day at La Jolla Shores, visiting UCSD, Birch Aquarium or Scripps Pier, shopping at UTC, hiking Torrey Pines, or dining in the Village or Bird Rock.</p>
    <p>Browse the full menu for fast pickup: <a href="/shop">shop cannabis products</a>.</p>
  `,
  box2Header: 'Dispensary Deals Near La Jolla',
  box2Content: `
    <p>Searching for dispensary deals near me in La Jolla? Catalyst is known statewide for offering real savings — not inflated prices with fake discounts.</p>
    <p>Expect deals like:</p>
    <ul>
      <li>BOGOs on select pre-rolls and vapes</li>
      <li>20–40% off brand spotlights</li>
      <li>Daily rotation deals</li>
      <li>Discounts on Catalyst house brands</li>
      <li>Seasonal promotions &amp; holiday specials</li>
      <li>Loyalty rewards for return customers</li>
    </ul>
    <p>Browse today's promos: <a href="/deals">cannabis deals</a>.</p>
  `,
  box3Header: 'Why Catalyst?',
  box3Content: `
    <p>Catalyst isn't just another dispensary near La Jolla — we're a cannabis destination built on integrity, transparency, and deep product knowledge. Our shelves are stocked with carefully selected brands, our staff is highly trained, and our pricing is always fair and straightforward.</p>
    <p>From premium strains to wellness-focused tinctures to high-potency concentrates, Catalyst offers a refined yet accessible cannabis experience for La Jolla shoppers.</p>
    <p><strong>Quality you can see. Prices you'll appreciate. Service you can trust.</strong></p>
  `,
  box4Header: 'What to Know About Buying Weed in and Around La Jolla',
  box4Content: `
    <p>Anyone 21+ with a valid government-issued ID can shop at a recreational dispensary. California law allows adults to purchase up to the legal daily limit for flower, concentrates, edibles, and vapes.</p>
    <p>Whether you're a La Jolla resident, student, or visitor, Catalyst provides a secure, legal, and seamless shopping experience.</p>
  `,
  faqs: [
    {
      question: 'How far is Catalyst from La Jolla?',
      answer: 'Catalyst is approximately 15–20 minutes from most La Jolla neighborhoods.',
    },
    {
      question: 'Can I get directions from La Jolla to Catalyst?',
      answer:
        'Yes — use your preferred maps app or visit our <a href="/dispensary-locations">find a dispensary</a> page.',
    },
    {
      question: 'Is Catalyst a recreational or medical dispensary?',
      answer: 'Catalyst is a fully licensed recreational dispensary for adults 21+.',
    },
    {
      question: 'How much cannabis can I buy?',
      answer:
        'California allows up to the daily legal limit for flower, vapes, edibles, and concentrates.',
    },
    {
      question: 'What are the dispensary hours?',
      answer: "Hours vary, so check your nearest Catalyst location's online menu.",
    },
    {
      question: 'What discounts are available?',
      answer:
        'Catalyst offers rotating daily deals, loyalty savings, holiday promos, and house-brand discounts.',
    },
    {
      question: 'What types of payments do you accept?',
      answer: 'Most locations accept cash and debit; ATMs are available onsite.',
    },
    {
      question: 'Can I order online for pickup?',
      answer: 'Yes — online ordering is fast, easy, and recommended for convenience.',
    },
  ],
};

export interface NearMePageMeta {
  title: string;
  description: string;
  canonical: string;
  ogTitle: string;
}

export function buildNearMePageMeta(page: NearMePageData, urlSlug: string): NearMePageMeta {
  return {
    title: page.seoTitle || 'Dispensary Near You | Catalyst Cannabis',
    description:
      page.seoDescription ||
      'Find a top-rated Catalyst Cannabis dispensary near you. Premium cannabis at fair prices, with daily deals and online ordering.',
    canonical: `https://catalyst-cannabis.com/dispensary-locations/${urlSlug}`,
    ogTitle: page.ogTitle || page.seoTitle || 'Dispensary Near You | Catalyst Cannabis',
  };
}

interface DispensaryNearMePageProps {
  page: NearMePageData;
  /**
   * Promotional carousel for the deals slot, fetched per-request in the loader
   * with the user's cookie-selected store. Lives outside `page` because
   * `NearMePageData` is cached per slug — carousel data must vary per store.
   */
  dealsCarousel?: Carousel | null;
}

export default function DispensaryNearMePage({
  page,
  dealsCarousel,
}: DispensaryNearMePageProps) {
  // Elfsight Google Reviews widget — same pattern as the location page.
  // The matched store's reviews_widget HTML contains a <div class="elfsight-app-XXX">.
  // We extract the class, render an empty div, and lazy-load the platform script
  // so it hydrates the widget client-side.
  const elfsightWidgetClass =
    page.matchedLocationReviewsWidget.match(/class="(elfsight-app-[^"]+)"/)?.[1] || null;

  useEffect(() => {
    if (!elfsightWidgetClass) return;
    if (
      document.querySelector('script[src*="static.elfsight.com/platform/platform.js"]')
    ) {
      const eapps = (window as unknown as { eapps?: { Platform?: { init: () => void } } })
        .eapps;
      try {
        eapps?.Platform?.init();
      } catch {
        /* ignore re-init errors */
      }
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://static.elfsight.com/platform/platform.js';
    script.async = true;
    document.body.appendChild(script);
  }, [elfsightWidgetClass]);

  const breadcrumbItems = [
    { name: 'Home', url: '/' },
    { name: 'Locations', url: '/dispensary-locations' },
    {
      name: `Near ${page.city}`,
      url: `/dispensary-locations/near-${page.slug}-ca`,
    },
  ];

  // `cms-rich-text` carries headings, images, blockquotes and tables — the
  // elements the editors can produce that this class never covered. The
  // remaining utilities stay because they set this surface's own type and
  // colour, which the shared class deliberately leaves alone.
  const richTextClass =
    'cms-rich-text font-helvetica-neue leading-relaxed text-gray-700 [&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-800 [&_li]:mb-1 [&_ol]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-4 [&_p]:leading-6 [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_strong]:font-semibold';

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: page.faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.answer.replace(/<[^>]+>/g, ''),
      },
    })),
  };

  // This page sends people to a real store in a neighbouring city, so the markup
  // describes THAT store — near-cerritos-ca points at Cherry Ave, so the Store
  // block is Cherry Ave. `areaServed` is what carries the city this page is
  // about; inventing a store in it would be a guidelines violation.
  //
  // Built by the same generateLocationSchema the store pages use. This block
  // used to be four hand-written fields — name, a bare address string,
  // telephone, url — with no PostalAddress, geo, hours or rating, purely
  // because it was a second implementation that never kept up.
  const schemaData = page.storeSchemaData;
  const addressSegments = (page.matchedLocationAddress || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  const fallbackCity =
    addressSegments.length >= 2 ? addressSegments[addressSegments.length - 2] : page.city;
  const fallbackStreet =
    addressSegments.length >= 2
      ? addressSegments.slice(0, -2).join(', ')
      : page.matchedLocationAddress;

  const localBusinessSchema = generateLocationSchema({
    name: page.matchedLocationName,
    url: `https://catalyst-cannabis.com/dispensary-locations/${page.matchedLocationSlug}`,
    address: {
      streetAddress: schemaData?.addressParts?.street || fallbackStreet,
      addressLocality: schemaData?.addressParts?.city || fallbackCity,
      addressRegion: schemaData?.addressParts?.state || 'CA',
      postalCode:
        schemaData?.addressParts?.zip ||
        (page.matchedLocationAddress || '').match(/\d{5}/)?.[0] ||
        '',
    },
    phone: page.matchedLocationPhone || undefined,
    email: schemaData?.email || undefined,
    geo: schemaData?.geo
      ? { latitude: schemaData.geo.lat, longitude: schemaData.geo.lng }
      : undefined,
    hasMap: schemaData?.mapsUrl || page.matchedLocationDirectionsUrl || undefined,
    // Deliberately omitted. The store pages carry aggregateRating because they
    // print "4.8 (1,932 Reviews)" next to the store name; these pages do not
    // show a rating anywhere — the Elfsight widget renders individual reviews
    // client-side, which is not the same thing. Marking up a rating the visitor
    // cannot see is the manual-action risk, so the data is plumbed through
    // (page.storeSchemaData carries it) and left unused until the template
    // displays it. Add the visible rating first, then pass it here.
    areaServed: [page.city],
    priceRange: '$$',
    paymentAccepted: ['Cash', 'Debit Card'],
    currenciesAccepted: 'USD',
  });

  return (
    <div className="flex w-full flex-col items-center bg-white">
      {/* JSON-LD */}
      <script
        type="application/ld+json"
        suppressHydrationWarning
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        suppressHydrationWarning
        dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }}
      />

      {/* Breadcrumbs */}
      <div className="w-full px-4 pt-4">
        <div className="mx-auto max-w-[90rem]">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-[90rem] flex-col gap-8 px-4 pb-12">
        {/* 1. Hero */}
        <section className="flex flex-col items-start gap-4 bg-[#F8FAFC] rounded-lg px-6 py-8 lg:px-10 lg:py-12">
          <h1 className="font-poppins text-[32px] leading-10 font-bold text-[#1E293B] lg:text-[44px] lg:leading-[52px]">
            {page.header}
          </h1>
          {/* Intro Text is authored in Budflow's rich editor, so it may be
              HTML — a link, a heading, a list. Rendering it as a text node
              escaped that markup and showed the raw tags to the visitor, and a
              link in particular simply never appeared as one. That is the
              "intro section doesn't show links" report.

              Both shapes are still in the data: rows written before the field
              became a rich editor are plain text whose paragraphs are blank
              lines, and collapsing those into HTML would run them together. So
              the shape is detected rather than assumed — a tag means HTML,
              anything else keeps the blank-line splitting it has always had. */}
          <div className="font-helvetica-neue max-w-3xl text-base leading-7 text-[#374151] lg:text-lg">
            {/<[a-z][\s\S]*>/i.test(page.introText) ? (
              <div
                className="cms-rich-text"
                dangerouslySetInnerHTML={{ __html: page.introText }}
              />
            ) : (
              page.introText
                .split(/\n{2,}/)
                .filter(Boolean)
                .map((para, i) => (
                  <p key={i} className="mb-4 last:mb-0">
                    {para}
                  </p>
                ))
            )}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <Link
              to="/shop"
              className="font-helvetica-neue rounded-lg bg-[#264B9A] px-6 py-3 text-sm font-semibold uppercase text-white hover:bg-blue-700"
            >
              Shop Now
            </Link>
            <Link
              to={`/dispensary-locations/${page.matchedLocationSlug}`}
              className="font-helvetica-neue rounded-lg px-6 py-3 text-sm font-semibold uppercase text-[#264B9A] ring-2 ring-[#264B9A] hover:bg-blue-50"
            >
              Visit {page.matchedLocationName}
            </Link>
          </div>
        </section>

        {/* 2. Shop Cannabis Products (box1) */}
        <section className="flex flex-col gap-3 px-2">
          <h2 className="font-poppins text-[28px] font-semibold text-[#1E293B]">
            {page.box1Header}
          </h2>
          <div
            className={richTextClass}
            dangerouslySetInnerHTML={{ __html: page.box1Content }}
          />
        </section>

        {/* 3. How to Get Here (directions) */}
        <section className="flex flex-col gap-3 px-2">
          <h2 className="font-poppins text-[28px] font-semibold text-[#1E293B]">
            {page.directionsHeader}
          </h2>
          <div
            className={richTextClass}
            dangerouslySetInnerHTML={{ __html: page.directionsContent }}
          />
        </section>

        {/* 4. Map / Directions module — pulls matched-location address & embed */}
        <section className="flex w-full flex-col items-start gap-4 px-2">
          <div className="flex items-center gap-3">
            <img src="/locations/Marker.svg" alt="" className="h-6 w-6" />
            <h2 className="font-poppins text-[28px] font-semibold text-[#1E293B]">
              Directions &amp; Location — {page.matchedLocationName}
            </h2>
          </div>
          <div className="flex w-full flex-col gap-4 lg:flex-row">
            <div className="lg:w-2/3">
              <div
                dangerouslySetInnerHTML={{ __html: page.matchedLocationMapEmbed }}
                className="h-[412px] w-full overflow-hidden rounded-lg border border-slate-300 [&_iframe]:h-[412px] [&_iframe]:w-full"
              />
            </div>
            <aside className="flex flex-col gap-3 rounded-lg bg-[#F8FAFC] p-6 lg:w-1/3">
              <p className="font-poppins text-lg font-semibold text-[#1E293B]">
                {page.matchedLocationName}
              </p>
              {page.matchedLocationAddress && (
                <a
                  href={page.matchedLocationDirectionsUrl || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Get directions to ${page.matchedLocationName}`}
                  className="font-helvetica-neue text-[#374151] underline hover:text-[#264B9A]"
                >
                  {page.matchedLocationAddress}
                </a>
              )}
              {page.matchedLocationHours && (
                <p className="font-helvetica-neue text-[#374151]">
                  {page.matchedLocationHours}
                </p>
              )}
              {page.matchedLocationPhone && (
                <a
                  href={`tel:${page.matchedLocationPhone.replace(/[^+\d]/g, '')}`}
                  aria-label={`Call ${page.matchedLocationName}`}
                  className="font-helvetica-neue text-[#264B9A] hover:underline"
                >
                  {page.matchedLocationPhone}
                </a>
              )}
              <Link
                to={`/dispensary-locations/${page.matchedLocationSlug}`}
                className="font-helvetica-neue mt-2 inline-block rounded-lg bg-[#264B9A] px-4 py-2 text-center text-sm font-semibold uppercase text-white hover:bg-blue-700"
              >
                View store page
              </Link>
            </aside>
          </div>
        </section>

        {/* 5. Local Deals — promotional carousel for the user's selected store.
           No `contained` prop and no extra padding so the carousel fills the
           page's existing max-w-[90rem] wrapper. */}
        {dealsCarousel && (
          <section className="flex w-full flex-col gap-3">
            <PromotionalCarousel data={dealsCarousel} />
          </section>
        )}

        {/* 6. Dispensary Deals copy (box2) */}
        <section className="flex flex-col gap-3 px-2">
          <h2 className="font-poppins text-[28px] font-semibold text-[#1E293B]">
            {page.box2Header}
          </h2>
          <div
            className={richTextClass}
            dangerouslySetInnerHTML={{ __html: page.box2Content }}
          />
        </section>

        {/* 7. Customer Reviews — Elfsight Google Reviews widget for the matched store */}
        {elfsightWidgetClass && (
          <section id="customer-reviews" className="flex flex-col gap-3 px-2">
            <div className="flex items-center gap-3">
              <img src="/locations/cannabis-Star.svg" alt="" className="h-6 w-6" />
              <h2 className="font-poppins text-[28px] font-semibold text-[#1E293B]">
                Customer Reviews
              </h2>
            </div>
            <div className="reviews-widget-container overflow-hidden">
              <div className={elfsightWidgetClass} data-elfsight-app-lazy />
            </div>
          </section>
        )}

        {/* 8. Why Catalyst (box3) */}
        <section className="flex flex-col gap-3 px-2">
          <h2 className="font-poppins text-[28px] font-semibold text-[#1E293B]">
            {page.box3Header}
          </h2>
          <div
            className={richTextClass}
            dangerouslySetInnerHTML={{ __html: page.box3Content }}
          />
        </section>

        {/* 9. What to Know (box4) */}
        <section className="flex flex-col gap-3 px-2">
          <h2 className="font-poppins text-[28px] font-semibold text-[#1E293B]">
            {page.box4Header}
          </h2>
          <div
            className={richTextClass}
            dangerouslySetInnerHTML={{ __html: page.box4Content }}
          />
        </section>

        {/* 10. FAQs */}
        <section className="flex flex-col gap-3 px-2">
          <FaqsSection
            faqs={page.faqs}
            locationName={page.city}
            showLocationSpecificContent={false}
            includeSchema={false}
          />
        </section>
      </div>
    </div>
  );
}
